/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/
import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import { Html } from "../html";
import {
  getExtraMetadataItems,
  getLearnMoreContent,
} from "../../modules/layers/utils";
import type { DescriptionFields } from "../../modules/layers/model";
import { addBlankToLinks } from "../../utils";
import { COMPACT_VIEWPORT } from "../../theme";

export interface ConsolidatedDescriptionProps {
  descriptionFields: DescriptionFields;
  t: (key: string) => string;
  language?: string;
  translationReviewed?: boolean;
}

export const ConsolidatedDescription: React.FC<
  ConsolidatedDescriptionProps
> = ({ descriptionFields, t, language, translationReviewed }) => {
  const theme = useTheme();
  const {
    description,
    readMoreLinks,
    downloadLinks,
    sources,
    citation,
    license,
    disclaimer,
  } = descriptionFields;

  const otherSections = [
    {
      title: t("Learn More"),
      content: getLearnMoreContent(readMoreLinks, downloadLinks),
    },
    { title: t("Source(s)"), content: sources },
    { title: t("Suggested Citation"), content: citation },
    { title: t("Disclaimer"), content: disclaimer },
    { title: t("License"), content: license },
  ];

  const validOtherSections = otherSections.filter(
    (section) => !!section.content
  );

  const otherSectionsHtml = validOtherSections
    .map((section) => {
      const contentHtml = `<p>${addBlankToLinks(section.content)}</p>`;
      return `<h6 style="padding-top: 0.5em">${section.title}</h6>${contentHtml}`;
    })
    .join("");

  const descriptionHtml = description
    ? `<p>${addBlankToLinks(description)}</p>`
    : "";

  const extraMetadataItems = getExtraMetadataItems(
    descriptionFields,
    t,
    language
  );

  return (
    <Box
      sx={{
        [COMPACT_VIEWPORT]: {
          "&& p, && li, && a, && .MuiTypography-root, && .MuiAlert-message": {
            fontSize: "0.85rem",
          },
          "&& h1, && h2, && h3, && h4, && h5, && h6": {
            fontSize: "0.95rem",
          },
          "&& .MuiAlert-icon .MuiSvgIcon-root": {
            fontSize: "1.1rem",
          },
        },
      }}
    >
      {translationReviewed === false && (
        <Alert severity="info" sx={{ mb: 2, alignItems: "center" }}>
          {t(
            "The translation of this layer's information was generated automatically and may contain inaccuracies."
          )}
        </Alert>
      )}
      {descriptionHtml && <Html html={descriptionHtml} />}
      {extraMetadataItems.length > 0 && (
        <Box sx={{ marginTop: 1, marginBottom: 1 }}>
          <Divider />
          <Box sx={{ marginTop: 1, marginBottom: 1 }}>
            <Grid container spacing={1}>
              {extraMetadataItems.map(({ label, value }, index) => (
                <Grid key={index} size={6}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        display: "block",
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography variant="body2">{value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
          <Divider />
        </Box>
      )}
      {otherSectionsHtml && <Html html={otherSectionsHtml} />}
    </Box>
  );
};
